import { useAuth } from '../api/AuthContext';
import { useCallback, useMemo } from 'react';

/**
 * Mapeo de nombres de módulos antiguos a nuevos (migración sidebar 2026)
 */
const MODULE_MIGRATION_MAP = {
  'Gestión Escolar': 'Control Escolar',
  'Gestión de Estudiantes': 'Perfil Académico',
  'Gestión Financiera': 'Finanzas',
  'Administración': 'Administración del Sistema',
  'Catálogos': 'Administración del Sistema',
  'Reportes': 'Otros',
};

/**
 * Normaliza el nombre del módulo (traduce nombres antiguos a nuevos)
 */
const normalizeModule = (modulo) => {
  return MODULE_MIGRATION_MAP[modulo] || modulo;
};

/**
 * Obtiene posibles aliases de un módulo (legacy/canónico)
 */
const getModuleCandidates = (modulo) => {
  if (!modulo) return [];

  const candidates = new Set();
  const add = (value) => {
    if (typeof value === 'string' && value.trim()) {
      candidates.add(value);
    }
  };

  const normalized = normalizeModule(modulo);
  add(modulo);
  add(normalized);

  Object.entries(MODULE_MIGRATION_MAP).forEach(([legacyModule, canonicalModule]) => {
    if (
      modulo === legacyModule ||
      modulo === canonicalModule ||
      normalized === legacyModule ||
      normalized === canonicalModule
    ) {
      add(legacyModule);
      add(canonicalModule);
    }
  });

  return Array.from(candidates);
};

/**
 * Resuelve la llave de módulo existente en permisos con fallback por aliases
 */
const resolvePermissionModule = (permisos, modulo) => {
  const candidates = getModuleCandidates(modulo);
  for (const candidate of candidates) {
    if (permisos[candidate]) {
      return candidate;
    }
  }
  return normalizeModule(modulo);
};

/**
 * Mapeo de rutas a módulos de permisos
 */
export const ROUTE_TO_MODULE = {
  // Consulta
  '/consulta/estudiantes': 'Consulta',

  // Control Escolar (antes Gestión Escolar)
  '/programas': 'Control Escolar',
  '/materias': 'Control Escolar',
  '/asignacion-programa-materia': 'Control Escolar',
  '/estudiantes': 'Control Escolar',
  '/tramites-escolares': 'Control Escolar',
  
  // Perfil Académico (antes parte de Gestión Escolar y Gestión de Estudiantes)
  '/docentes': 'Perfil Académico',
  '/grupos': 'Perfil Académico',
  '/preasignacion-materias': 'Perfil Académico',
  '/asignacion-grupos': 'Perfil Académico',
  
  // Administración del Sistema (antes Catálogos y Administración)
  '/instituciones': 'Administración del Sistema',
  '/campus': 'Administración del Sistema',
  '/secciones-academicas': 'Administración del Sistema',
  '/periodos': 'Administración del Sistema',
  '/ciclos-escolares': 'Administración del Sistema',
  '/conceptos-cobro': 'Administración del Sistema',
  '/becas': 'Administración del Sistema',
  '/academias': 'Administración del Sistema',
  '/gestion-usuarios': 'Administración del Sistema',
  '/roles-permisos': 'Administración del Sistema',
  // Reportes de captura/carga (movidos a Perfil Académico)
  '/reporte-calificaciones': 'Perfil Académico',
  '/reporte-carga-academica': 'Perfil Académico',
  '/reporte-seguros-estudiantes': 'Finanzas',
  '/reporte-seguros-asesores': 'Finanzas',
  
  // Finanzas (antes Gestión Financiera y parte de Gestión de Estudiantes)
  '/inscripcion-reinscripcion': 'Finanzas',
  '/asignacion-becas': 'Finanzas',
  '/generacion-cargos-manuales': 'Finanzas',
  '/generacion-cargos-automaticos': 'Finanzas',
  '/aplicacion-pagos': 'Finanzas',
  '/reporte-ingresos-diarios': 'Finanzas',
  '/reporte-adeudos': 'Finanzas',
  '/reporte-rectoria': 'Administración del Sistema',
  '/reporte-estudiantes-nuevo-ingreso': 'Administración del Sistema',
  
  // Admisiones
  '/admision-estudiantes': 'Admisiones',
  '/gestion-interesados': 'Admisiones',
  
  // Otros
  '/cambio-grupos': 'Otros',
  '/baja-reactivacion': 'Otros',
  '/cierre-ciclo-escolar': 'Otros',
  '/portal-estudiantes': 'Otros',
  '/reporte-grupos-estudiantes': 'Otros',
  
  // Panel Asesor
  '/registro-calificaciones': 'Panel Asesor',
  '/registro-asistencias': 'Panel Asesor',
};

/**
 * Hook para verificar permisos del usuario actual
 * 
 * Uso:
 * const { canView, canEdit, canDelete, hasPermission } = usePermissions();
 * 
 * if (canView('Gestión Escolar')) {
 *   // Mostrar contenido
 * }
 */
export function usePermissions() {
  const { user } = useAuth();
  
  const permisos = useMemo(() => {
    return user?.permisos || {};
  }, [user]);

  /**
   * Verificar si tiene permiso de lectura en un módulo
   */
  const canView = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    return permisos[moduleKey]?.puede_leer === true;
  }, [permisos]);

  /**
   * Verificar si tiene permiso de edición en un módulo
   */
  const canEdit = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    return permisos[moduleKey]?.puede_actualizar === true;
  }, [permisos]);

  /**
   * Verificar si tiene permiso de eliminación en un módulo
   */
  const canDelete = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    return permisos[moduleKey]?.puede_eliminar === true;
  }, [permisos]);

  /**
   * Verificar si tiene permiso de creación en un módulo
   */
  const canCreate = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    return permisos[moduleKey]?.puede_crear === true;
  }, [permisos]);

  /**
   * Verificar si tiene permiso de aprobación en un módulo
   */
  const canApprove = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    return permisos[moduleKey]?.puede_aprobar === true;
  }, [permisos]);

  /**
   * Verificar un permiso específico
   * @param {string} modulo - Nombre del módulo
   * @param {string} permiso - Tipo de permiso: 'leer', 'crear', 'actualizar', 'eliminar', 'aprobar'
   */
  const hasPermission = useCallback((modulo, permiso) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    const permisoModulo = permisos[moduleKey];
    if (!permisoModulo) return false;
    
    switch (permiso) {
      case 'leer':
      case 'ver':
        return permisoModulo.puede_leer === true;
      case 'crear':
        return permisoModulo.puede_crear === true;
      case 'actualizar':
      case 'editar':
        return permisoModulo.puede_actualizar === true;
      case 'eliminar':
        return permisoModulo.puede_eliminar === true;
      case 'aprobar':
        return permisoModulo.puede_aprobar === true;
      default:
        return false;
    }
  }, [permisos]);

  /**
   * Verificar si tiene algún permiso en un módulo
   */
  const hasAnyPermission = useCallback((modulo) => {
    const moduleKey = resolvePermissionModule(permisos, modulo);
    const permisoModulo = permisos[moduleKey];
    if (!permisoModulo) return false;
    
    return (
      permisoModulo.puede_leer === true ||
      permisoModulo.puede_crear === true ||
      permisoModulo.puede_actualizar === true ||
      permisoModulo.puede_eliminar === true ||
      permisoModulo.puede_aprobar === true
    );
  }, [permisos]);

  /**
   * Obtener todos los módulos donde el usuario tiene al menos permiso de lectura
   */
  const getAccessibleModules = useCallback(() => {
    return Object.keys(permisos).filter(modulo => permisos[modulo]?.puede_leer === true);
  }, [permisos]);

  return {
    permisos,
    canView,
    canEdit,
    canDelete,
    canCreate,
    canApprove,
    hasPermission,
    hasAnyPermission,
    getAccessibleModules
  };
}

export default usePermissions;
